import React, { useState, useEffect } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "حاسبة الكثافة - حساب الكثافة والكتلة والحجم بدقة";
const DESCRIPTION =
  "حاسبة الكثافة المجانية - احسب الكثافة أو الكتلة أو الحجم بسهولة. أداة دقيقة تدعم مختلف الوحدات (كجم، جم، م³، سم³) مع أمثلة عملية ومعلومات تعليمية عن الكثافة.";
const KEYWORDS =
  "حاسبة الكثافة, حساب الكتلة, حساب الحجم, قانون الكثافة, تحويل وحدات الكثافة, density calculator, حساب الوزن والكتلة";
const OG_URL = "https://do-calculate.com/calculator/ar/density/";

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "حاسبة الكثافة",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "UtilityApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "الرئيسية",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "الحاسبات",
        item: "https://do-calculate.com/ar/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "حاسبة الكثافة",
        item: OG_URL,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "ما هو قانون الكثافة؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "قانون الكثافة هو الكثافة (ρ) = الكتلة (m) / الحجم (V). تُقاس الكثافة عادة بوحدات كجم/م³ أو جم/سم³.",
        },
      },
      {
        "@type": "Question",
        name: "كيف أحسب الكتلة إذا عرفت الكثافة والحجم؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "يمكنك حساب الكتلة بضرب الكثافة في الحجم (الكتلة = الكثافة × الحجم). على سبيل المثال، إذا كانت الكثافة 5 جم/سم³ والحجم 10 سم³، فإن الكتلة تكون 50 جراماً.",
        },
      },
    ],
  },
];

const CALC_MODES = {
  density: "حساب الكثافة (ρ)",
  mass: "حساب الكتلة (m)",
  volume: "حساب الحجم (V)",
};

const MASS_UNITS = {
  kg: { name: "كيلوجرام (kg)", factor: 1000 },
  g: { name: "جرام (g)", factor: 1 },
  mg: { name: "مليجرام (mg)", factor: 0.001 },
  lb: { name: "رطل (lb)", factor: 453.592 },
  oz: { name: "أونصة (oz)", factor: 28.3495 },
};

const VOLUME_UNITS = {
  m3: { name: "متر مكعب (m³)", factor: 1000000 },
  cm3: { name: "سنتيمتر مكعب (cm³)", factor: 1 },
  ml: { name: "مليلتر (ml)", factor: 1 },
  l: { name: "لتر (L)", factor: 1000 },
  in3: { name: "بوصة مكعبة (in³)", factor: 16.3871 },
  ft3: { name: "قدم مكعبة (ft³)", factor: 28316.8 },
};

const DENSITY_UNITS = {
  kgm3: { name: "كجم/م³", toGcm3: 0.001 },
  gcm3: { name: "جم/سم³", toGcm3: 1 },
  lbft3: { name: "رطل/قدم³", toGcm3: 0.0160185 },
};

function DensityCalculator() {
  const [mode, setMode] = useState("density");
  const [mass, setMass] = useState("");
  const [massUnit, setMassUnit] = useState("kg");
  const [volume, setVolume] = useState("");
  const [volumeUnit, setVolumeUnit] = useState("m3");
  const [density, setDensity] = useState("");
  const [densityUnit, setDensityUnit] = useState("kgm3");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const calculate = () => {
    setError("");
    try {
      if (mode === "density") {
        const m = parseFloat(mass) * MASS_UNITS[massUnit].factor; // to grams
        const v = parseFloat(volume) * VOLUME_UNITS[volumeUnit].factor; // to cm3
        if (v <= 0 || isNaN(m) || isNaN(v)) return;

        const dGcm3 = m / v;
        setResult({
          value: dGcm3,
          type: "density",
        });
      } else if (mode === "mass") {
        const d = parseFloat(density) * DENSITY_UNITS[densityUnit].toGcm3; // to g/cm3
        const v = parseFloat(volume) * VOLUME_UNITS[volumeUnit].factor; // to cm3
        if (d <= 0 || v <= 0 || isNaN(d) || isNaN(v)) return;

        const mGrams = d * v;
        setResult({
          value: mGrams,
          type: "mass",
        });
      } else if (mode === "volume") {
        const m = parseFloat(mass) * MASS_UNITS[massUnit].factor; // to grams
        const d = parseFloat(density) * DENSITY_UNITS[densityUnit].toGcm3; // to g/cm3
        if (m <= 0 || d <= 0 || isNaN(m) || isNaN(d)) return;

        const vCm3 = m / d;
        setResult({
          value: vCm3,
          type: "volume",
        });
      }
    } catch (err) {
      setError("يرجى التأكد من صحة المدخلات");
    }
  };

  useEffect(() => {
    calculate();
  }, [mode, mass, massUnit, volume, volumeUnit, density, densityUnit]);

  const handleClear = () => {
    setMass("");
    setVolume("");
    setDensity("");
    setResult(null);
    setError("");
  };

  return (
    <div className="rtl" style={{ direction: "rtl" }}>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div className="bg-success text-white text-center" style={{ padding: "15px", borderRadius: "5px" }}>
            <h1 style={{ fontSize: "22px", margin: 0 }}>حاسبة الكثافة</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                <strong>حاسبة الكثافة</strong> هي أداة متكاملة تتيح لك حساب <strong>الكثافة</strong>، <strong>الكتلة</strong>، أو <strong>الحجم</strong> بسهولة فائقة. سواء كنت طالباً أو مهندساً، يمكنك الآن إيجاد المتغير المفقود بمجرد إدخال القيمتين المعلومتين. تدعم الحاسبة مجموعة واسعة من الوحدات العالمية لضمان الدقة في النتائج العلمية والصناعية.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />

            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>اختر المتغير المطلوب حسابه</h2>
              </div>

              <div className="row mb-3">
                <div className="col-12">
                  <label >ماذا تريد أن تحسب؟</label>
                  <select className="form-select" value={mode} onChange={(e) => { setMode(e.target.value); handleClear(); }}>
                    {Object.entries(CALC_MODES).map(([key, value]) => (
                      <option key={key} value={key}>{value}</option>
                    ))}
                  </select>
                </div>
              </div>

              {mode !== "mass" && (
                <div className="row mb-3">
                  <div className="col-md-8">
                    <label >الكتلة (Mass)</label>
                    <input type="number" className="form-control" value={mass} onChange={(e) => setMass(e.target.value)} placeholder="أدخل الكتلة" />
                  </div>
                  <div className="col-md-4">
                    <label >الوحدة</label>
                    <select className="form-select" value={massUnit} onChange={(e) => setMassUnit(e.target.value)}>
                      {Object.entries(MASS_UNITS).map(([key, unit]) => (
                        <option key={key} value={key}>{unit.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {mode !== "volume" && (
                <div className="row mb-3">
                  <div className="col-md-8">
                    <label >الحجم (Volume)</label>
                    <input type="number" className="form-control" value={volume} onChange={(e) => setVolume(e.target.value)} placeholder="أدخل الحجم" />
                  </div>
                  <div className="col-md-4">
                    <label >الوحدة</label>
                    <select className="form-select" value={volumeUnit} onChange={(e) => setVolumeUnit(e.target.value)}>
                      {Object.entries(VOLUME_UNITS).map(([key, unit]) => (
                        <option key={key} value={key}>{unit.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {mode !== "density" && (
                <div className="row mb-3">
                  <div className="col-md-8">
                    <label >الكثافة (Density)</label>
                    <input type="number" className="form-control" value={density} onChange={(e) => setDensity(e.target.value)} placeholder="أدخل الكثافة" />
                  </div>
                  <div className="col-md-4">
                    <label >الوحدة</label>
                    <select className="form-select" value={densityUnit} onChange={(e) => setDensityUnit(e.target.value)}>
                      {Object.entries(DENSITY_UNITS).map(([key, unit]) => (
                        <option key={key} value={key}>{unit.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              <div className="text-center mb-3 mt-4">
                <button onClick={handleClear} className="btn btn-secondary" style={{ padding: "10px 30px" }}>امسح البيانات</button>
              </div>

              {error && <div className="alert alert-warning">{error}</div>}

              {result && (
                <div className="mt-4">
                  <h3 className="text-center mb-3">النتيجة النهائية</h3>
                  <table className="table bmi-table">
                    <thead>
                      <tr>
                        <th className="bmi-header text-center">الوحدة</th>
                        <th className="bmi-header text-center">القيمة</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.type === "density" && (
                        <>
                          <tr>
                            <td className="text-center">كجم/متر مكعب (kg/m³)</td>
                            <td className="text-center font-weight-bold">{(result.value * 1000).toLocaleString()}</td>
                          </tr>
                          <tr>
                            <td className="text-center">جرام/سنتيمتر مكعب (g/cm³)</td>
                            <td className="text-center font-weight-bold">{result.value.toFixed(4)}</td>
                          </tr>
                        </>
                      )}
                      {result.type === "mass" && (
                        <>
                          <tr>
                            <td className="text-center">كيلوجرام (kg)</td>
                            <td className="text-center font-weight-bold">{(result.value / 1000).toFixed(4)}</td>
                          </tr>
                          <tr>
                            <td className="text-center">جرام (g)</td>
                            <td className="text-center font-weight-bold">{result.value.toFixed(2)}</td>
                          </tr>
                        </>
                      )}
                      {result.type === "volume" && (
                        <>
                          <tr>
                            <td className="text-center">متر مكعب (m³)</td>
                            <td className="text-center font-weight-bold">{(result.value / 1000000).toFixed(6)}</td>
                          </tr>
                          <tr>
                            <td className="text-center">سنتيمتر مكعب (cm³)</td>
                            <td className="text-center font-weight-bold">{result.value.toFixed(2)}</td>
                          </tr>
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <hr />
            <RelatedLinks />
          </div>
        </div>
        <div className="col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>

      <div className="row mt-4">
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">ما هي حاسبة الكثافة؟ وكيف تستخدمها؟</div>
              <div className="info-section-body">
                <p>حاسبة الكثافة هي أداة تعليمية وعملية صُممت لتبسيط العمليات الحسابية المتعلقة بالكتلة والحجم والكثافة. تعتمد الحاسبة على العلاقة الفيزيائية الأساسية التي تربط بين هذه العناصر الثلاثة.</p>
                <p><strong>خطوات الاستخدام:</strong></p>
                <ol>
                  <li>اختر ما تود حسابه من القائمة المنسدلة (كثافة، كتلة، أو حجم).</li>
                  <li>أدخل القيمتين الأخريين في الحقول المخصصة.</li>
                  <li>تأكد من اختيار الوحدات الصحيحة لكل قيمة (مثل كجم للكتلة أو لتر للحجم).</li>
                  <li>ستقوم الحاسبة فوراً بعرض النتيجة بوحدات مختلفة لتسهيل المقارنة.</li>
                </ol>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">استخدامات حاسبة الكثافة في الواقع</div>
              <div className="info-section-body">
                <ul>
                  <li><strong>التعليم والعلوم:</strong> حل المسائل الفيزيائية والكيميائية للطلاب في المدارس والجامعات.</li>
                  <li><strong>الهندسة والبناء:</strong> حساب وزن المواد الإنشائية مثل الخرسانة والحديد بناءً على حجمها.</li>
                  <li><strong>الصناعة والتجارة:</strong> تحديد نقاوة المعادن والسبائك من خلال مقارنة كثافتها المحسوبة بالكثافة القياسية.</li>
                  <li><strong>الشحن والخدمات اللوجستية:</strong> تقدير أحجام الحاويات المطلوبة لشحن كتل معينة من البضائع السائلة أو الصلبة.</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">مفاهيم أساسية في الكثافة</div>
              <p>الكثافة هي مقدار ما يحتويه الحجم من كتلة. وتختلف كثافة المواد باختلاف نوعها وحالتها الفيزيائية.</p>
              <table className="table bmi-table mt-2">
                <thead>
                  <tr>
                    <th>المادة</th>
                    <th>الكثافة التقريبية (جم/سم³)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>الماء العذب</td><td>1.00</td></tr>
                  <tr><td>الذهب</td><td>19.32</td></tr>
                  <tr><td>الحديد</td><td>7.87</td></tr>
                  <tr><td>الألمنيوم</td><td>2.70</td></tr>
                  <tr><td>الهواء (عند سطح البحر)</td><td>0.001225</td></tr>
                </tbody>
              </table>
            </div>

            <div className="info-section">
              <div className="info-section-header">أمثلة عمليّة</div>
              <div className="info-section-body">
                <p><strong>مثال 1:</strong> إذا كان لديك قطعة ذهب كتلتها 193.2 جرام وحجمها 10 سم³، فما هي كثافتها؟<br />النتيجة: 193.2 / 10 = 19.32 جم/سم³.</p>
                <p><strong>مثال 2:</strong> ما هي كتلة لتر واحد من الزيت إذا كانت كثافته 0.9 كجم/لتر؟<br />النتيجة: 0.9 × 1 = 0.9 كجم (أي 900 جرام).</p>
                <p><strong>مثال 3:</strong> قطعة من الحديد كتلتها 7.87 كجم، كم سيكون حجمها إذا علمنا أن كثافة الحديد 7.87 جم/سم³؟<br />النتيجة: الحجم سيكون 1000 سم³ (أي لتر واحد).</p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">نصائح للاستخدام الصحيح</div>
              <div className="info-section-body">
                <ul>
                  <li>انتبه دائماً لتحويل الوحدات؛ الحاسبة تقوم بذلك تلقائياً إذا اخترت الوحدة من القائمة.</li>
                  <li>تذكر أن درجة الحرارة تؤثر على الكثافة (خاصة للغازات والسوائل)، لذا فإن القيم المعطاة عادة ما تكون عند درجة حرارة الغرفة.</li>
                  <li>في حالة المواد المسامية (مثل الخشب)، تأكد مما إذا كنت تحسب الكثافة الكلية أم كثافة المادة الصلبة فقط.</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">الأسئلة الشائعة حول الكثافة</div>
              <div className="info-section-body">
                <p><strong>1. هل الكثافة هي نفس الوزن؟</strong><br />لا، الكثافة خاصية للمادة تعبر عن تركيز الكتلة في حجم معين، بينما الوزن هو قوة جذب الأرض لتلك الكتلة.</p>
                <p><strong>2. لماذا يطفو الجليد فوق الماء؟</strong><br />لأن كثافة الجليد أقل من كثافة الماء السائل، حيث يتمدد الماء عند التجمد فيزداد حجمه وتقل كثافته.</p>
                <p><strong>3. ما هي وحدة الكثافة في النظام الدولي؟</strong><br />الوحدة الرسمية هي كيلوجرام لكل متر مكعب (kg/m³).</p>
                <p><strong>4. كيف تؤثر الحرارة على الكثافة؟</strong><br />غالباً ما تؤدي الحرارة إلى تمدد الأجسام (زيادة الحجم)، مما يؤدي إلى انخفاض الكثافة مع بقاء الكتلة ثابتة.</p>
                <p><strong>5. ما الفرق بين الكتلة والوزن في حسابات الكثافة؟</strong><br />في العمليات الحسابية العلمية نستخدم الكتلة (بالكجم أو الجرام) لأنها ثابتة لا تتغير بتغير المكان عكس الوزن.</p>
                <p><strong>6. هل للهواء كثافة؟</strong><br />نعم، للهواء كثافة رغم صغرها، وهي التي تسمح للطائرات والمناطيد بالتحليق بناءً على مبدأ دفع الهواء.</p>
                <p><strong>7. كيف أحسب كثافة جسم غير منتظم؟</strong><br />عن طريق غمره في مخبار مدرج يحتوي على ماء، وحساب مقدار ارتفاع مستوى الماء (الذي يمثل حجم الجسم) ثم قسمة كتلته على هذا الحجم.</p>
                <p><strong>8. ما هي كثافة الماء المالح؟</strong><br />كثافة الماء المالح أكبر من الماء العذب (حوالي 1.025 جم/سم³)، ولهذا يسهل الطفو في البحر الميت مثلاً.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DensityCalculator;